import { useEffect } from "react";

const WRAPPER_CLASS = "lrn-table-wrapper";

/**
 * Wraps every `.lrn table` in a scrollable `div.lrn-table-wrapper`.
 *
 * Watches for DOM mutations inside `#content` and wraps newly-mounted
 * Learnosity tables. Only runs once Learnosity is ready.
 */
const useLearnosityTableWrap = (isLearnosityReady: boolean): void => {
  useEffect(() => {
    if (!isLearnosityReady) return;

    const container = document.getElementById("content");
    if (!container) return;

    const wrapAllTables = () => {
      const tables = container.querySelectorAll<HTMLTableElement>(".lrn table");
      for (const table of tables) {
        const parent = table.parentElement;
        if (!parent || parent.classList.contains(WRAPPER_CLASS)) continue;

        const wrapper = document.createElement("div");
        wrapper.className = WRAPPER_CLASS;
        table.before(wrapper);
        wrapper.appendChild(table);
      }
    };

    let scheduled = false;
    const scheduleWrap = () => {
      if (scheduled) return;
      scheduled = true;
      setTimeout(() => {
        scheduled = false;
        wrapAllTables();
      }, 0);
    };

    const observer = new MutationObserver(() => {
      scheduleWrap();
    });

    wrapAllTables();
    observer.observe(container, {
      childList: true,
      subtree: true,
      characterData: true
    });

    return () => observer.disconnect();
  }, [isLearnosityReady]);
};

export default useLearnosityTableWrap;
